"use client";

import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Sparkles, Moon, Sun, Zap } from "lucide-react";
import { db } from "@/lib/db";
import type { AiProfile } from "@/types/ai";
import { allThemePresets } from "@/lib/themes/presets";
import { haptics } from "@/lib/utils/haptics";

interface Props {
  open: boolean;
  profile: AiProfile;
  onClose: () => void;
  onProfileUpdate: (p: AiProfile) => void;
}

const UNIVERSE_PRESETS = [
  { key: "cosmic",   label: "Cosmic",   desc: "Stars, nebula & particles",     preview: "radial-gradient(ellipse at 30% 30%, #1a0533 0%, #070b1a 60%, #000 100%)" },
  { key: "aurora",   label: "Aurora",   desc: "Northern lights shimmer",        preview: "linear-gradient(135deg, #0d1f2d 0%, #0a2a1a 40%, #1a0d2e 100%)" },
  { key: "gradient", label: "Gradient", desc: "Pure color gradient, no 3D",     preview: "linear-gradient(135deg, #1a0533 0%, #0f172a 100%)" },
  { key: "minimal",  label: "Minimal",  desc: "Dark glass, almost nothing",     preview: "linear-gradient(180deg, #0a0a12 0%, #0d0d18 100%)" },
  { key: "void",     label: "Void",     desc: "Absolute black, no distraction", preview: "#000000" },
] as const;

const GLASS_STYLES = [
  { key: "dark",    label: "Dark",    preview: "rgba(10,10,22,0.85)" },
  { key: "frosted", label: "Frosted", preview: "rgba(30,30,50,0.6)" },
  { key: "light",   label: "Light",   preview: "rgba(240,238,255,0.82)" },
] as const;

const COLOR_CATEGORY_LABELS: Record<string, string> = {
  premium: "Signature",
  metallic: "Metallic",
  pastel: "Pastel",
};

export default function MindalooAppearancePanel({ open, profile, onClose, onProfileUpdate }: Props) {
  const accent = profile.brand?.primaryColor ?? "#C1A2FF";
  const secondary = profile.brand?.secondaryColor ?? "#9333EA";
  const mt = profile.brand?.mindalooTheme ?? {};

  const [universe, setUniverse] = useState<string>(mt.universe ?? "cosmic");
  const [dayNight, setDayNight] = useState<"night" | "day">(mt.dayNight ?? "night");
  const [glassStyle, setGlassStyle] = useState<string>(mt.glassStyle ?? "dark");
  const [accentGlow, setAccentGlow] = useState(mt.accentGlow ?? true);
  const [primaryColor, setPrimaryColor] = useState(accent);
  const [secondaryColor, setSecondaryColor] = useState(secondary);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<"universe" | "color" | "glass">("universe");

  const applyPreset = useCallback((primary: string, sec: string) => {
    setPrimaryColor(primary);
    setSecondaryColor(sec);
    haptics.click();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const updated: AiProfile = {
      ...profile,
      brand: {
        ...profile.brand,
        primaryColor,
        secondaryColor,
        mindalooTheme: {
          universe: universe as "cosmic" | "aurora" | "minimal" | "void" | "gradient",
          dayNight,
          glassStyle: glassStyle as "dark" | "light" | "frosted",
          accentGlow,
        },
      },
      updatedAt: Date.now(),
    };
    await db.aiProfiles.update(profile.id, updated);
    onProfileUpdate(updated);
    setSaving(false);
    setSaved(true);
    haptics.success();
    setTimeout(() => { setSaved(false); onClose(); }, 900);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="appearance-backdrop"
            className="fixed inset-0 z-[1800] bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            key="appearance-panel"
            className="fixed inset-x-0 bottom-0 z-[1810] flex flex-col"
            style={{
              top: "max(24px, 4dvh)",
              borderTopLeftRadius: 28,
              borderTopRightRadius: 28,
              background: "rgba(8, 8, 18, 0.97)",
              backdropFilter: "blur(40px) saturate(200%)",
              WebkitBackdropFilter: "blur(40px) saturate(200%)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderBottom: "none",
              boxShadow: `0 -24px 80px rgba(0,0,0,0.6), 0 0 0 1px ${primaryColor}18 inset`,
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 360, damping: 34 }}
          >
            {/* Top accent line */}
            <div
              className="absolute top-0 left-0 right-0 h-px rounded-t-full"
              style={{ background: `linear-gradient(90deg, transparent, ${primaryColor}60, ${secondaryColor}60, transparent)` }}
            />

            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1 shrink-0">
              <div className="w-10 h-1 rounded-full bg-white/15" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 shrink-0">
              <div>
                <h2 className="text-sm font-semibold text-white/90 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                  Appearance
                </h2>
                <p className="text-[11px] text-white/35 mt-0.5">Personalise Mindaloo&apos;s look &amp; feel</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.09)" }}
              >
                <X className="w-3.5 h-3.5 text-white/50" />
              </button>
            </div>

            {/* Live preview strip */}
            <div
              className="mx-5 mb-4 rounded-2xl h-14 shrink-0 flex items-center justify-between px-4"
              style={{
                background: `linear-gradient(135deg, ${primaryColor}22 0%, ${secondaryColor}18 100%)`,
                border: `1px solid ${primaryColor}30`,
                boxShadow: accentGlow ? `0 0 24px ${primaryColor}25` : "none",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }} />
                <div>
                  <p className="text-[11px] font-semibold text-white/80">Preview</p>
                  <p className="text-[10px] text-white/35">{primaryColor}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => { setDayNight(d => d === "night" ? "day" : "night"); haptics.click(); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all"
                  style={{
                    background: dayNight === "day" ? "rgba(250,204,21,0.15)" : "rgba(139,92,246,0.12)",
                    border: `1px solid ${dayNight === "day" ? "rgba(250,204,21,0.3)" : "rgba(139,92,246,0.2)"}`,
                    color: dayNight === "day" ? "#facc15" : "#a78bfa",
                  }}
                >
                  {dayNight === "night" ? <Moon className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
                  {dayNight === "night" ? "Night" : "Day"}
                </button>
                <button
                  type="button"
                  onClick={() => { setAccentGlow(g => !g); haptics.click(); }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all"
                  style={{
                    background: accentGlow ? `${primaryColor}18` : "rgba(255,255,255,0.04)",
                    border: `1px solid ${accentGlow ? primaryColor + "40" : "rgba(255,255,255,0.08)"}`,
                    color: accentGlow ? primaryColor : "rgba(255,255,255,0.4)",
                  }}
                >
                  <Zap className="w-3 h-3" />
                  Glow
                </button>
              </div>
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 px-5 mb-4 shrink-0">
              {(["universe", "color", "glass"] as const).map(tab => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className="px-4 py-1.5 rounded-full text-[11px] font-medium capitalize transition-all"
                  style={activeTab === tab ? {
                    background: `${primaryColor}20`,
                    border: `1px solid ${primaryColor}50`,
                    color: primaryColor,
                  } : {
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.45)",
                  }}
                >
                  {tab === "universe" ? "Background" : tab === "color" ? "Accent Color" : "Glass Style"}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-32 scrollbar-hide">
              <AnimatePresence mode="wait">

                {activeTab === "universe" && (
                  <motion.div key="universe" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {UNIVERSE_PRESETS.map(preset => (
                      <button
                        key={preset.key}
                        type="button"
                        onClick={() => { setUniverse(preset.key); haptics.click(); }}
                        className="relative rounded-2xl overflow-hidden text-left transition-all"
                        style={{
                          border: universe === preset.key ? `1.5px solid ${primaryColor}` : "1.5px solid rgba(255,255,255,0.07)",
                          boxShadow: universe === preset.key ? `0 0 20px ${primaryColor}30` : "none",
                        }}
                      >
                        <div className="h-20" style={{ background: preset.preview }} />
                        <div className="px-3 py-2" style={{ background: "rgba(10,10,20,0.92)" }}>
                          <p className="text-[11px] font-semibold text-white/85">{preset.label}</p>
                          <p className="text-[10px] text-white/35 mt-0.5 leading-tight">{preset.desc}</p>
                        </div>
                        {universe === preset.key && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: primaryColor }}>
                            <Check className="w-2.5 h-2.5 text-black" />
                          </div>
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}

                {activeTab === "color" && (
                  <motion.div key="color" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-5">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-white/40 uppercase tracking-widest mb-2">Primary</p>
                        <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
                          <div className="w-8 h-8 rounded-lg shrink-0" style={{ background: primaryColor, boxShadow: `0 0 12px ${primaryColor}60` }} />
                          <span className="text-[11px] text-white/60 font-mono">{primaryColor}</span>
                          <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="opacity-0 absolute w-0 h-0" />
                        </label>
                      </div>
                      <div>
                        <p className="text-[10px] text-white/40 uppercase tracking-widest mb-2">Secondary</p>
                        <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
                          <div className="w-8 h-8 rounded-lg shrink-0" style={{ background: secondaryColor, boxShadow: `0 0 12px ${secondaryColor}60` }} />
                          <span className="text-[11px] text-white/60 font-mono">{secondaryColor}</span>
                          <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} className="opacity-0 absolute w-0 h-0" />
                        </label>
                      </div>
                    </div>

                    {(["premium", "metallic", "pastel"] as const).map(cat => (
                      <div key={cat}>
                        <p className="text-[10px] text-white/35 uppercase tracking-widest mb-2">{COLOR_CATEGORY_LABELS[cat]}</p>
                        <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                          {allThemePresets.filter(p => p.category === cat).map(preset => {
                            const isActive = primaryColor === preset.primary && secondaryColor === preset.secondary;
                            return (
                              <button
                                key={preset.key}
                                type="button"
                                title={preset.name}
                                onClick={() => applyPreset(preset.primary, preset.secondary)}
                                className="relative flex flex-col items-center gap-1 p-2 rounded-xl transition-all"
                                style={{
                                  background: isActive ? `${preset.primary}20` : "rgba(255,255,255,0.03)",
                                  border: isActive ? `1.5px solid ${preset.primary}` : "1.5px solid rgba(255,255,255,0.06)",
                                }}
                              >
                                <div className="w-8 h-8 rounded-full" style={{ background: `linear-gradient(135deg, ${preset.primary}, ${preset.secondary})` }} />
                                <span className="text-[9px] text-white/45 leading-tight text-center">{preset.name}</span>
                                {isActive && (
                                  <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: preset.primary }}>
                                    <Check className="w-2 h-2 text-black" />
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}

                {activeTab === "glass" && (
                  <motion.div key="glass" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="grid grid-cols-3 gap-3">
                    {GLASS_STYLES.map(style => (
                      <button
                        key={style.key}
                        type="button"
                        onClick={() => { setGlassStyle(style.key); haptics.click(); }}
                        className="relative flex flex-col items-center gap-3 p-4 rounded-2xl transition-all"
                        style={{
                          background: glassStyle === style.key ? `${primaryColor}12` : "rgba(255,255,255,0.03)",
                          border: glassStyle === style.key ? `1.5px solid ${primaryColor}` : "1.5px solid rgba(255,255,255,0.07)",
                          boxShadow: glassStyle === style.key ? `0 0 20px ${primaryColor}25` : "none",
                        }}
                      >
                        <div className="w-full h-16 rounded-xl" style={{ background: style.preview, backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.12)" }} />
                        <span className="text-[11px] font-medium text-white/70">{style.label}</span>
                        {glassStyle === style.key && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: primaryColor }}>
                            <Check className="w-2.5 h-2.5 text-black" />
                          </div>
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}

              </AnimatePresence>
            </div>

            {/* Save button */}
            <div
              className="absolute bottom-0 inset-x-0 px-5 pb-8 pt-4 shrink-0"
              style={{ background: "linear-gradient(to top, rgba(8,8,18,0.98) 70%, transparent)" }}
            >
              <motion.button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="w-full py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-all"
                style={{
                  background: saved ? "rgba(34,197,94,0.2)" : `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                  color: saved ? "#22c55e" : "#000",
                  border: saved ? "1px solid rgba(34,197,94,0.4)" : "none",
                  boxShadow: saved ? "none" : `0 8px 32px ${primaryColor}40`,
                  opacity: saving ? 0.7 : 1,
                }}
                whileTap={{ scale: 0.97 }}
              >
                {saved ? (
                  <><Check className="w-4 h-4" /> Saved</>
                ) : saving ? (
                  <><div className="w-4 h-4 border-2 border-black/30 border-t-black/80 rounded-full animate-spin" /> Saving…</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Apply Appearance</>
                )}
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
